import React, { useState } from 'react';
import { generateInsights } from '../utils/openai';
import { Bot, RefreshCw, Search, Target, AlertTriangle, Sparkles, TrendingUp, Calendar, Lightbulb } from 'lucide-react';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const AIInsights = ({ reviews }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  const generateNewInsights = async () => {
    if (reviews.length === 0) {
      setError('No reviews available for analysis');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const newInsights = await generateInsights(reviews);
      setInsights(newInsights);
      setLastGenerated(new Date().toLocaleString());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove automatic loading - let user trigger it manually

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      case 'mixed': return '😐';
      default: return '🤔';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      case 'mixed': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="ai-insights">
        <div className="insights-header">
          <h2>🤖 AI Insights</h2>
        </div>
        <div className="loading-ai">
          <div className="loading-spinner"></div>
          <p>Analyzing reviews with AI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-insights">
        <div className="insights-header">
          <h2>🤖 AI Insights</h2>
          <button 
            className="refresh-btn"
            onClick={generateNewInsights}
            disabled={loading}
          >
            🔄 Retry
          </button>
        </div>
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="ai-insights">
        <div className="insights-header">
          <h2>🤖 AI Insights</h2>
          <button 
            className="generate-btn"
            onClick={generateNewInsights}
            disabled={loading || reviews.length === 0}
          >
            Generate Insights
          </button>
        </div>
        <p>Click "Generate Insights" to analyze your reviews with AI.</p>
      </div>
    );
  }

  return (
    <div className="ai-insights">
      <div className="insights-header">
        <div>
          <h2><Bot size={24} /> AI Insights</h2>
          <p className="data-scope">Analyzing {formatNumber(reviews.length)} reviews</p>
          {reviews.length < 100 && (
            <p className="filter-notice"><Search size={14} /> Filtered data - adjust filters above to change scope</p>
          )}
          {lastGenerated && (
            <p className="last-generated">Last updated: {lastGenerated}</p>
          )}
        </div>
        <button 
          className="refresh-btn"
          onClick={generateNewInsights}
          disabled={loading}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="insights-grid">
        {/* Sentiment Overview */}
        <div className="insight-card">
          <h3><TrendingUp size={20} /> Sentiment Overview</h3>
          
          {/* Overall Metrics */}
          <div className="sentiment-metrics">
            <div className="metric-row">
              <div className="metric-item">
                <span className="metric-label">Overall Rating</span>
                <span className="metric-value">{insights.sentimentTrends?.averageRating || 'N/A'}/5</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Total Reviews</span>
                <span className="metric-value">{formatNumber(insights.sentimentTrends?.totalReviews || 0)}</span>
              </div>
            </div>
          </div>

          {/* Sentiment Breakdown */}
          <div className="sentiment-breakdown">
            <h4>Sentiment Distribution:</h4>
            <div className="sentiment-bars">
              <div className="sentiment-bar positive">
                <span className="bar-label">😊 Positive</span>
                <div className="bar-container">
                  <div 
                    className="bar-fill positive"
                    style={{ width: `${insights.sentimentTrends?.positivePercentage || 0}%` }}
                  ></div>
                </div>
                <span className="bar-value">{insights.sentimentTrends?.positivePercentage || 0}%</span>
              </div>
              <div className="sentiment-bar neutral">
                <span className="bar-label">😐 Neutral</span>
                <div className="bar-container">
                  <div 
                    className="bar-fill neutral"
                    style={{ width: `${insights.sentimentTrends?.neutralPercentage || 0}%` }}
                  ></div>
                </div>
                <span className="bar-value">{insights.sentimentTrends?.neutralPercentage || 0}%</span>
              </div>
              <div className="sentiment-bar negative">
                <span className="bar-label">😟 Negative</span>
                <div className="bar-container">
                  <div 
                    className="bar-fill negative"
                    style={{ width: `${insights.sentimentTrends?.negativePercentage || 0}%` }}
                  ></div>
                </div>
                <span className="bar-value">{insights.sentimentTrends?.negativePercentage || 0}%</span>
              </div>
            </div>
          </div>
          
          {/* Segment Performance */}
          <div className="segment-performance">
            <h4>Performance by Segment:</h4>
            <div className="segment-grid">
              {Object.entries(insights.sentimentTrends?.segmentBreakdown || {}).map(([segment, data]) => (
                <div key={segment} className="segment-card">
                  <div className="segment-header">
                    <span className="segment-name">{segment}</span>
                    <span className="segment-rating">{data.avgRating}/5</span>
                  </div>
                  <div className="segment-bars">
                    <div className="mini-bar positive" style={{ width: `${data.positive}%` }} title={`${data.positive}% positive`}></div>
                    <div className="mini-bar neutral" style={{ width: `${data.neutral}%` }} title={`${data.neutral}% neutral`}></div>
                    <div className="mini-bar negative" style={{ width: `${data.negative}%` }} title={`${data.negative}% negative`}></div>
                  </div>
                  <div className="segment-stats">
                    <span className="positive-stat">{data.positive}% positive</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Themes */}
        <div className="insight-card">
          <h3><Target size={20} /> Key Themes</h3>
          <div className="themes-list">
            {insights.keyThemes?.slice(0, 5).map((theme, index) => (
              <div key={index} className="theme-item">
                <div className="theme-header">
                  <span className="theme-name">{theme.theme}</span>
                  <span 
                    className="theme-sentiment"
                    style={{ color: getSentimentColor(theme.sentiment) }}
                  >
                    {getSentimentIcon(theme.sentiment)}
                  </span>
                </div>
                <div className="theme-details">
                  <span className={`frequency-badge ${theme.frequency}`}>
                    {theme.frequency} frequency
                  </span>
                  <span className="theme-segments">
                    {theme.segments?.join(', ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Complaints */}
        <div className="insight-card">
          <h3><AlertTriangle size={20} /> Common Complaints</h3>
          <div className="complaints-list">
            {insights.commonComplaints?.slice(0, 4).map((complaint, index) => (
              <div key={index} className="complaint-item">
                <div className="complaint-text">{complaint.issue}</div>
                <div className="complaint-meta">
                  <span className={`frequency-badge ${complaint.frequency}`}>
                    {complaint.frequency}
                  </span>
                  <span className="affected-segments">
                    {complaint.segments?.join(', ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Compliments */}
        <div className="insight-card">
          <h3><Sparkles size={20} /> Common Compliments</h3>
          <div className="compliments-list">
            {insights.commonCompliments?.slice(0, 4).map((compliment, index) => (
              <div key={index} className="compliment-item">
                <div className="compliment-text">{compliment.praise}</div>
                <div className="compliment-meta">
                  <span className={`frequency-badge ${compliment.frequency}`}>
                    {compliment.frequency}
                  </span>
                  <span className="affected-segments">
                    {compliment.segments?.join(', ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Seasonal Patterns */}
        <div className="insight-card">
          <h3><Calendar size={20} /> Seasonal Patterns</h3>
          <div className="patterns-list">
            {insights.seasonalPatterns?.map((pattern, index) => (
              <div key={index} className="pattern-item">
                <div className="pattern-text">{pattern.pattern}</div>
                <div className="pattern-meta">
                  <span className="timeframe">{pattern.timeframe}</span>
                  <span 
                    className="impact"
                    style={{ color: pattern.impact === 'positive' ? '#10b981' : '#ef4444' }}
                  >
                    {pattern.impact === 'positive' ? '📈' : '📉'} {pattern.impact}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actionable Recommendations */}
        <div className="insight-card recommendations">
          <h3><Lightbulb size={20} /> Value Enhancement Recommendations</h3>
          <div className="recommendations-list">
            {insights.actionableRecommendations?.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <div className="recommendation-header">
                  <span 
                    className="priority-indicator"
                    style={{ backgroundColor: getPriorityColor(rec.priority) }}
                  >
                    {rec.priority?.toUpperCase()}
                  </span>
                  <span className="focus-badge">💎 Value Enhancement</span>
                </div>
                <div className="recommendation-text">{rec.recommendation}</div>
                <div className="recommendation-segments">
                  Affects: {rec.segments?.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInsights;